import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, CheckCircle, Star, CheckCheck, Search, Palette, Code, Link2, ShieldCheck, Rocket, TrendingUp as TrendUp, Brain, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import RotatingBanner from '../components/RotatingBanner';
import TestimonialCarousel from '../components/TestimonialCarousel';
import { Handshake, Network, TrendingUp } from 'lucide-react';
import { companyInfo, caseStudies } from '../data/mock';
import { freightManagementCaseStudy } from '../data/servicesData';
import SEO from '../components/SEO';
import * as LucideIcons from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Process steps data
const processSteps = [
  {
    id: 1,
    title: 'AI-Driven Discovery',
    description: 'We use AI-assisted analysis to understand business goals, data readiness, user behavior, and risks.',
    outcome: 'Faster insights, sharper problem definition.',
    icon: Search,
    color: '#320547'
  },
  {
    id: 2,
    title: 'Intelligent Design',
    description: 'AI helps evaluate architectures, optimize workflows, and simulate outcomes before we build.',
    outcome: 'Smarter, scalable solution blueprints.',
    icon: Palette,
    color: '#4a1a5e'
  },
  {
    id: 3,
    title: 'Accelerated Build',
    description: 'AI-assisted development, automation, and code quality checks speed up delivery without compromise.',
    outcome: 'High-quality builds, delivered faster.',
    icon: Code,
    color: '#5c2370'
  },
  {
    id: 4,
    title: 'Smart Integration',
    description: 'AI validates data flows, detects anomalies, and ensures seamless system interoperability.',
    outcome: 'Reliable, connected ecosystems.',
    icon: Link2,
    color: '#6e2c82'
  },
  {
    id: 5,
    title: 'AI-Led Validation',
    description: 'Automated testing, performance prediction, and edge-case detection using AI.',
    outcome: 'Fewer defects, higher confidence.',
    icon: ShieldCheck,
    color: '#803594'
  },
  {
    id: 6,
    title: 'Predictive Deployment',
    description: 'AI supports rollout planning, monitors health, and flags risks in real time.',
    outcome: 'Stable, low-risk go-live.',
    icon: Rocket,
    color: '#8f3da3'
  },
  {
    id: 7,
    title: 'Continuous Intelligence',
    description: 'AI continuously learns from usage, performance, and feedback to optimize outcomes.',
    outcome: 'Systems that improve over time.',
    icon: TrendUp,
    color: '#95228A'
  }
];

// Process Animator Component - Circular Design
const ProcessAnimator = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % processSteps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHovered]);

  const step = processSteps[currentStep];
  const Icon = step.icon;

  // Calculate positions for circular layout
  const getStepPosition = (index, total) => {
    const angle = (index * 360 / total) - 90; // Start from top
    const radius = 140;
    const x = Math.cos((angle * Math.PI) / 180) * radius;
    const y = Math.sin((angle * Math.PI) / 180) * radius;
    return { x, y, angle };
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main Container with Background Image */}
      <div className="relative rounded-3xl shadow-2xl overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80)` 
          }}
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#1a0a20]/75" />

        {/* Content */}
        <div className="relative z-10 p-8">
          {/* Circular Process Diagram */}
          <div className="relative flex items-center justify-center" style={{ height: '380px' }}>
            
            {/* Connecting Ring - Behind icons (z-0) */}
            <div className="absolute z-0 w-72 h-72 rounded-full border-2 border-dashed border-[#95228A]/30" />
            
            {/* Animated Progress Ring - Behind icons (z-0) */}
            <svg className="absolute z-0 w-72 h-72" viewBox="0 0 288 288">
              <circle
                cx="144"
                cy="144"
                r="142"
                fill="none"
                stroke="rgba(149, 34, 138, 0.15)"
                strokeWidth="4"
              />
              <motion.circle
                cx="144"
                cy="144"
                r="142"
                fill="none"
                stroke="url(#gradientBrand)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${((currentStep + 1) / processSteps.length) * 892} 892`}
                transform="rotate(-90 144 144)"
                initial={{ strokeDasharray: `${(currentStep / processSteps.length) * 892} 892` }}
                animate={{ strokeDasharray: `${((currentStep + 1) / processSteps.length) * 892} 892` }}
                transition={{ duration: 0.5 }}
              />
              <defs>
                <linearGradient id="gradientBrand" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#95228A" />
                  <stop offset="100%" stopColor="#320547" />
                </linearGradient>
              </defs>
            </svg>

            {/* Center - Abstract Globe (z-20) */}
            <div className="absolute z-20 w-44 h-44 rounded-full overflow-hidden shadow-2xl shadow-[#95228A]/40 flex items-center justify-center bg-gradient-to-br from-[#320547] to-[#1a0a20]">
              
              {/* Animated Wireframe Globe */}
              <div className="absolute inset-0 flex items-center justify-center">
                {/* Rotating outer ring */}
                <motion.div
                  className="absolute w-40 h-40 rounded-full border border-[#95228A]/40"
                  animate={{ rotateY: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  style={{ transformStyle: 'preserve-3d' }}
                />
                
                {/* Horizontal lines */}
                <div className="absolute w-36 h-36 rounded-full border border-[#95228A]/20" />
                <div className="absolute w-28 h-28 rounded-full border border-[#95228A]/30" />
                <div className="absolute w-20 h-20 rounded-full border border-[#95228A]/40" />
                
                {/* Vertical rotating ellipse 1 */}
                <motion.div
                  className="absolute w-36 h-36 rounded-full border border-[#95228A]/30"
                  animate={{ rotateY: 360 }}
                  transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                  style={{ transformStyle: 'preserve-3d', transform: 'rotateX(75deg)' }}
                />
                
                {/* Vertical rotating ellipse 2 */}
                <motion.div
                  className="absolute w-36 h-36 rounded-full border border-[#95228A]/25"
                  animate={{ rotateY: -360 }}
                  transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                  style={{ transformStyle: 'preserve-3d', transform: 'rotateX(75deg) rotateZ(60deg)' }}
                />
                
                {/* Vertical rotating ellipse 3 */}
                <motion.div
                  className="absolute w-36 h-36 rounded-full border border-[#95228A]/20"
                  animate={{ rotateY: 360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  style={{ transformStyle: 'preserve-3d', transform: 'rotateX(75deg) rotateZ(-60deg)' }}
                />
                
                {/* Center glow */}
                <div className="absolute w-16 h-16 rounded-full bg-gradient-to-br from-[#95228A]/60 to-[#320547]/40 blur-md" />
                
                {/* Pulsing core */}
                <motion.div
                  className="absolute w-8 h-8 rounded-full bg-gradient-to-br from-[#95228A] to-[#320547]"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
              
              {/* Overlay with current step info */}
              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-full z-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="flex flex-col items-center text-center"
                >
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Brain className="text-white mb-1 drop-shadow-lg" size={32} />
                  </motion.div>
                  <span className="text-white font-bold text-sm drop-shadow-lg leading-tight">AI-First</span>
                  <span className="text-white/80 text-xs font-medium">Delivery</span>
                </motion.div>
              </div>
            </div>

            {/* Step Nodes - Above the ring (z-10) */}
            {processSteps.map((s, index) => {
              const pos = getStepPosition(index, processSteps.length);
              const StepIcon = s.icon;
              const isActive = index === currentStep;
              const isPast = index < currentStep;
              
              // Calculate label position - push labels outward from center
              const labelAngle = (index * 360 / processSteps.length) - 90;
              const isTop = labelAngle >= -135 && labelAngle <= -45;
              const isBottom = labelAngle >= 45 && labelAngle <= 135;
              const isLeft = labelAngle > 135 || labelAngle < -135;
              const isRight = labelAngle > -45 && labelAngle < 45;
              
              return (
                <div
                  key={s.id}
                  className="absolute z-10"
                  style={{
                    transform: `translate(${pos.x}px, ${pos.y}px)`,
                  }}
                >
                  <button
                    onClick={() => setCurrentStep(index)}
                    className={`
                      transition-all duration-300 
                      ${isActive ? 'scale-110' : 'hover:scale-110'}
                    `}
                  >
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300
                      ${isActive 
                        ? 'bg-gradient-to-br from-[#95228A] to-[#320547] text-white ring-4 ring-[#95228A]/30 shadow-[#95228A]/40' 
                        : isPast 
                          ? 'bg-[#95228A]/20 text-[#95228A] backdrop-blur-sm border border-[#95228A]/30' 
                          : 'bg-white/5 text-gray-400 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:text-white'
                      }
                    `}>
                      <StepIcon size={22} />
                    </div>
                    {/* Label positioned based on angle */}
                    <span 
                      className={`
                        absolute text-xs font-medium whitespace-nowrap z-20
                        ${isActive ? 'text-[#95228A]' : 'text-gray-400'}
                        ${isTop ? 'bottom-full mb-2 left-1/2 -translate-x-1/2' : ''}
                        ${isBottom ? 'top-full mt-2 left-1/2 -translate-x-1/2' : ''}
                        ${isLeft ? 'right-full mr-3 top-1/2 -translate-y-1/2 text-right' : ''}
                        ${isRight ? 'left-full ml-3 top-1/2 -translate-y-1/2 text-left' : ''}
                        ${!isTop && !isBottom && !isLeft && !isRight ? 'top-full mt-2 left-1/2 -translate-x-1/2' : ''}
                      `}
                    >
                      {s.title}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Current Step Description */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mt-4 text-center"
            >
              <p className="text-gray-300 text-sm leading-relaxed max-w-sm mx-auto mb-3">
                {step.description}
              </p>
              <div className="inline-flex items-center gap-2 bg-[#95228A]/10 backdrop-blur-sm rounded-full px-4 py-2 border border-[#95228A]/20">
                <CheckCircle className="text-[#95228A]" size={16} />
                <span className="text-sm font-medium text-white">{step.outcome}</span>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Progress Indicator */}
          <div className="mt-6 flex justify-center gap-2">
            {processSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? 'w-8 bg-gradient-to-r from-[#95228A] to-[#320547]' 
                    : index < currentStep
                      ? 'w-1.5 bg-[#95228A]/50'
                      : 'w-1.5 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Success Stories Grid Component - Fetches from CMS
const SuccessStoriesGrid = ({ inView }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/success-stories?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setStories(data);
        }
      } catch (error) {
        console.error('Failed to fetch success stories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  // Fallback data if no CMS data
  const fallbackStories = [
    {
      id: '1',
      title: "Freight Management System",
      client: "Mera Transport",
      industry: "Logistics",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80",
      stat: "45%",
      stat_label: "Faster Dispatch"
    },
    {
      id: '2',
      title: "AI Suicide Prevention App",
      client: "MindGuard AI",
      industry: "Mental Health",
      image: "https://images.pexels.com/photos/5712677/pexels-photo-5712677.jpeg?w=600&q=80",
      stat: "10K+",
      stat_label: "Lives Supported"
    },
    {
      id: '3',
      title: "Kids Learning Platform",
      client: "Venslo",
      industry: "EdTech",
      image: "https://images.pexels.com/photos/4145035/pexels-photo-4145035.jpeg?w=600&q=80",
      stat: "500K+",
      stat_label: "Active Students"
    },
    {
      id: '4',
      title: "Cloud Migration",
      client: "HealthCare Plus",
      industry: "Healthcare",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80",
      stat: "99.9%",
      stat_label: "Uptime"
    },
    {
      id: '5',
      title: "At-Home Services Marketplace",
      client: "Promeets.us",
      industry: "Services",
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&q=80",
      stat: "50K+",
      stat_label: "Pros Connected"
    }
  ];

  const displayStories = stories.length > 0 ? stories : fallbackStories;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#95228A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Main Showcase Area */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        
        {/* Featured Story - Large Display */}
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
              className="relative h-[450px] lg:h-[500px] rounded-3xl overflow-hidden group"
            >
              {/* Background Image with Parallax Effect */}
              <motion.div 
                className="absolute inset-0"
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={displayStories[activeIndex]?.image}
                  alt={displayStories[activeIndex]?.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a] via-[#2a2a2a]/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#2a2a2a]/60 to-transparent" />
              
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#95228A]/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#320547]/30 rounded-full blur-2xl" />
              
              {/* Content */}
              <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-end">
                {/* Industry Badge */}
                <motion.span 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white text-sm font-medium w-fit mb-4"
                >
                  <span className="w-2 h-2 bg-[#95228A] rounded-full animate-pulse" />
                  {displayStories[activeIndex]?.industry}
                </motion.span>
                
                {/* Stats */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-4"
                >
                  <span className="text-5xl lg:text-6xl font-bold text-white drop-shadow-lg">
                    {displayStories[activeIndex]?.stat}
                  </span>
                  <span className="text-white/70 text-lg ml-3">{displayStories[activeIndex]?.stat_label}</span>
                </motion.div>
                
                {/* Title & Client */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h3 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                    {displayStories[activeIndex]?.client}
                  </h3>
                  <p className="text-white/80 text-lg max-w-lg">
                    {displayStories[activeIndex]?.title}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Story Selector - Right Side */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          {displayStories.slice(0, 5).map((story, index) => (
            <motion.button
              key={story.id}
              initial={{ opacity: 0, x: 20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => setActiveIndex(index)}
              className={`relative flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-300 group ${
                activeIndex === index 
                  ? 'bg-gradient-to-r from-[#2a2a2a] to-[#3a3a3a] shadow-lg shadow-black/20' 
                  : 'bg-white hover:bg-gray-50 border border-gray-100 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {/* Thumbnail */}
              <div className={`relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 ${
                activeIndex === index ? 'ring-2 ring-white/30' : ''
              }`}>
                <img
                  src={story.image}
                  alt={story.client}
                  className="w-full h-full object-cover"
                />
                {activeIndex === index && (
                  <div className="absolute inset-0 bg-[#2a2a2a]/20" />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    activeIndex === index 
                      ? 'bg-white/20 text-white' 
                      : 'bg-[#95228A]/10 text-[#95228A]'
                  }`}>
                    {story.industry}
                  </span>
                </div>
                <h4 className={`font-bold truncate ${
                  activeIndex === index ? 'text-white' : 'text-gray-900'
                }`}>
                  {story.client}
                </h4>
                <p className={`text-sm truncate ${
                  activeIndex === index ? 'text-white/70' : 'text-gray-500'
                }`}>
                  {story.title}
                </p>
              </div>
              
              {/* Stat Badge */}
              <div className={`text-right flex-shrink-0 ${
                activeIndex === index ? 'text-white' : 'text-[#320547]'
              }`}>
                <span className="text-xl font-bold block">{story.stat}</span>
                <span className={`text-xs ${activeIndex === index ? 'text-white/60' : 'text-gray-400'}`}>
                  {story.stat_label?.split(' ').slice(0, 2).join(' ')}
                </span>
              </div>
              
              {/* Active Indicator */}
              {activeIndex === index && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
                />
              )}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const [servicesRef, servicesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [caseStudiesRef, caseStudiesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [partners, setPartners] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [cmsServices, setCmsServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  useEffect(() => {
    // Fetch partners from API
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cms/partners?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setPartners(data);
        }
      } catch (error) {
        console.error('Failed to fetch partners:', error);
      }
    };
    
    // Fetch blog posts from API
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/posts`);
        if (response.ok) {
          const data = await response.json();
          // Filter only published posts and take first 3
          const publishedPosts = data.filter(post => post.is_published !== false);
          setBlogPosts(publishedPosts.slice(0, 3));
        }
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
      }
    };
    
    // Fetch services from CMS
    const fetchServices = async () => {
      try {
        setServicesLoading(true);
        const response = await fetch(`${API_URL}/api/cms/service-menu?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setCmsServices(data);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setServicesLoading(false);
      }
    };
    
    fetchPartners();
    fetchBlogPosts();
    fetchServices();
  }, []);

  // Helper function to get Lucide icon by name
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName] || LucideIcons.Code;
    return Icon;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Home"
        description="Transform your business with cutting-edge mobile app development, web development, custom software, IT staffing, and digital transformation services. Expert technology solutions delivered worldwide."
        keywords="mobile app development company, web development services, IT staffing solutions, custom software development, offshore development, staff augmentation, enterprise software development, digital transformation services, cloud computing services, AI ML development"
        canonicalUrl="/"
      />
      
      {/* Hero Section - Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 clip-hero">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src="https://customer-assets.emergentagent.com/job_dark-theme-navaris/artifacts/pg3hl6sl_abstract_video_for_website_hero_section_representing%20%283%29.mp4"
            type="video/mp4"
          />
        </video>

        {/* Semi-transparent Dark Overlay */}
        <div className="absolute inset-0 bg-[#3F1362]/70" />

        {/* Content */}
        <div className="relative container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-screen">
          <div className="flex-1 flex items-center">
            <div className="max-w-5xl text-center">
              <RotatingBanner />
            </div>
          </div>
          
          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 mb-20 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button asChild size="lg" className="text-lg px-8 py-6 bg-white text-[#320547] hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                Work With Us <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white hover:text-[#320547] bg-transparent">
              <Link to="/services">Explore Services</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* About Preview - Why Choose Navaris Digital */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Animated Process Display */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <ProcessAnimator />
            </motion.div>

            {/* Right Side - Modern Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Header */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4 }}
                  className="flex items-center gap-3 mb-4"
                >
                  <div className="h-px w-12 bg-gradient-to-r from-[#320547] to-[#95228A]" />
                  <span className="text-[#95228A] text-sm font-semibold tracking-wider uppercase">Our Approach</span>
                </motion.div>
                
                <motion.h2 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight"
                >
                  Our AI-First
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#320547] to-[#95228A]">
                    Delivery Process
                  </span>
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="text-gray-600 mt-3 text-sm leading-relaxed"
                >
                  Every decision. Every phase. Powered by intelligence.
                </motion.p>
              </div>

              {/* Stats Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  { value: '100%', label: 'Project Success', sublabel: 'On-time delivery' },
                  { value: '50+', label: 'Tech Experts', sublabel: 'Dedicated team' },
                  { value: '7+', label: 'Years Experience', sublabel: 'Industry expertise' },
                  { value: '24/7', label: 'Support', sublabel: 'Always available' }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                    className="bg-white rounded-2xl p-5 shadow-lg shadow-gray-200/50 border border-gray-100 hover:shadow-xl hover:border-[#95228A]/20 transition-all duration-300 group"
                  >
                    <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#320547] to-[#95228A] group-hover:scale-105 transition-transform duration-300">
                      {stat.value}
                    </div>
                    <div className="text-gray-900 font-semibold text-sm mt-1">{stat.label}</div>
                    <div className="text-gray-500 text-xs">{stat.sublabel}</div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Key Differentiators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="space-y-3"
              >
                {[
                  { icon: CheckCircle, text: 'End-to-end project ownership' },
                  { icon: CheckCircle, text: 'Transparent agile methodology' },
                  { icon: CheckCircle, text: 'Future-proof architecture' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-700">
                    <item.icon className="text-[#95228A] flex-shrink-0" size={18} />
                    <span className="text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="flex items-center gap-4"
              >
                <Button asChild size="lg" className="bg-gradient-to-r from-[#320547] to-[#95228A] hover:opacity-90 transition-opacity shadow-lg shadow-[#320547]/20">
                  <Link to="/about" className="flex items-center gap-2">
                    Explore Our Story
                    <ArrowRight size={18} />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-[#320547] text-[#320547] hover:bg-[#320547]/5">
                  <Link to="/contact">
                    Get in Touch
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section ref={servicesRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </motion.div>

          {/* Services Grid from CMS - Bento Layout */}
          {servicesLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin text-[#320547]" size={48} />
            </div>
          ) : cmsServices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No services available at this time.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* First Card - Tall/Featured (spans 2 rows) */}
              {cmsServices[0] && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5 }}
                  className="lg:row-span-2"
                >
                  <Link to={`/services/${cmsServices[0].slug}`} className="block h-full">
                    <div className="relative h-full min-h-[500px] rounded-2xl overflow-hidden group cursor-pointer">
                      {/* Background Image */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                        style={{ 
                          backgroundImage: `url(${cmsServices[0].image || cmsServices[0].hero_image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800'})` 
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#320547]/95 via-[#320547]/50 to-transparent" />
                      
                      {/* Content */}
                      <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                        <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5 group-hover:bg-white/30 transition-colors">
                          {React.createElement(getIcon(cmsServices[0].icon), { className: "text-white", size: 28 })}
                        </div>
                        <h3 className="text-3xl font-bold mb-3 group-hover:text-white/90 transition-colors">
                          {cmsServices[0].name}
                        </h3>
                        <p className="text-white/80 text-base mb-5 leading-relaxed line-clamp-3">
                          {cmsServices[0].description || cmsServices[0].hero_subtitle}
                        </p>
                        
                        {/* Features */}
                        {cmsServices[0].features && cmsServices[0].features.length > 0 && (
                          <ul className="space-y-2 mb-5">
                            {cmsServices[0].features.slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                                <CheckCircle size={14} className="text-white flex-shrink-0" />
                                <span className="line-clamp-1">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        
                        <span className="inline-flex items-center text-sm font-semibold text-white group-hover:underline">
                          Learn More <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              )}

              {/* Remaining Cards - 2x2 Grid */}
              {cmsServices.slice(1, 5).map((service, index) => {
                const IconComponent = getIcon(service.icon);
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: (index + 1) * 0.1 }}
                  >
                    <Link to={`/services/${service.slug}`} className="block h-full">
                      <Card className="h-full group hover:shadow-xl transition-all duration-300 border-gray-100 overflow-hidden hover:-translate-y-1">
                        {/* Service Image */}
                        <div className="relative h-40 overflow-hidden">
                          <div 
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                            style={{ 
                              backgroundImage: `url(${service.image || service.hero_image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800'})` 
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute bottom-3 left-3">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center group-hover:bg-[#320547] transition-colors">
                              <IconComponent className="text-white" size={20} />
                            </div>
                          </div>
                        </div>
                        
                        <CardContent className="p-5">
                          <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#320547] transition-colors line-clamp-1">
                            {service.name}
                          </h3>
                          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                            {service.description || service.hero_subtitle}
                          </p>
                          
                          {/* Features Preview - Compact */}
                          {service.features && service.features.length > 0 && (
                            <ul className="space-y-1 mb-3">
                              {service.features.slice(0, 2).map((feature, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-gray-500 text-xs">
                                  <CheckCircle size={10} className="text-[#320547] flex-shrink-0" />
                                  <span className="line-clamp-1">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          
                          <span className="inline-flex items-center text-sm font-semibold text-[#320547] group-hover:underline">
                            Learn More <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
          
          {/* View All Services Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-10"
          >
            <Button asChild size="lg" className="bg-[#320547] hover:bg-[#95228A]">
              <Link to="/services">
                View All Services <ArrowRight className="ml-2" size={18} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Partnership
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Building lasting relationships through trust, innovation, and mutual success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Strategic Alliance',
                description: 'We forge strategic partnerships that align with your business goals, ensuring long-term success and growth.',
                image: 'https://images.unsplash.com/photo-1758599543152-a73184816eba?w=800&q=80',
                icon: Handshake
              },
              {
                title: 'Technology Partners',
                description: 'Collaborate with industry-leading technology providers to deliver cutting-edge solutions for your business.',
                image: 'https://images.unsplash.com/photo-1768796372063-4da660e034b8?w=800&q=80',
                icon: Network
              },
              {
                title: 'Growth Together',
                description: 'Our partnership model focuses on mutual growth, shared knowledge, and continuous innovation.',
                image: 'https://images.unsplash.com/photo-1758691737082-abb40e6e3d05?w=800&q=80',
                icon: TrendingUp
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                  data-testid={`partnership-card-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {/* Image */}
                  <div className="relative h-44 overflow-hidden">
                    <div 
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                      style={{ backgroundImage: `url(${item.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2a2a2a]/80 to-transparent" />
                    
                    {/* Icon overlay */}
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Icon className="text-[#320547]" size={24} />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#320547] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Partner Logos */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-16"
          >
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">Our Technology Partners</h3>
            {partners.length > 0 ? (
              <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 lg:gap-16">
                {partners.map((partner, index) => (
                  <div 
                    key={partner.id || index}
                    className="flex flex-col items-center justify-center cursor-pointer group"
                    data-testid={`partner-logo-${partner.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <img 
                      src={partner.logo} 
                      alt={partner.name} 
                      className="w-20 h-20 md:w-24 md:h-24 object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300"
                    />
                    <span className="text-gray-400 text-sm mt-3 text-center leading-tight font-medium group-hover:text-gray-800 transition-colors duration-300">{partner.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400">Partner logos coming soon</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Case Studies / Success Stories */}
      <section ref={caseStudiesRef} className="py-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={caseStudiesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={caseStudiesInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#320547]/5 rounded-full px-4 py-2 mb-4"
            >
              <div className="w-2 h-2 bg-[#95228A] rounded-full animate-pulse" />
              <span className="text-[#320547] text-sm font-semibold">Proven Results</span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Success <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#320547] to-[#95228A]">Stories</span>
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Real transformations, measurable impact
            </p>
          </motion.div>

          {/* Success Stories Component */}
          <SuccessStoriesGrid inView={caseStudiesInView} />

          {/* View All CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={caseStudiesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-center mt-10"
          >
            <Button asChild variant="outline" size="sm" className="border-[#320547] text-[#320547] hover:bg-[#320547] hover:text-white">
              <Link to="/contact" className="flex items-center gap-2">
                Reach Us for Detailed Case Studies
                <ArrowRight size={16} />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Client Testimonials
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hear what our clients say about working with us
            </p>
          </motion.div>
          <TestimonialCarousel />
        </div>
      </section>

      {/* Blog Preview */}
      {blogPosts.length > 0 && (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Latest Insights
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay updated with our latest thoughts on technology and innovation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden h-full">
                  <img
                    src={post.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800'}
                    alt={post.title}
                    className="w-full h-48 object-cover"
                  />
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="text-[#320547] font-semibold">{post.category}</span>
                      <span>{post.date || new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="text-[#320547] font-semibold hover:underline inline-flex items-center"
                    >
                      Read More <ArrowRight className="ml-1" size={16} />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild variant="outline" size="lg">
              <Link to="/blog">View All Blog Posts</Link>
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1764534161906-f08540a2d333?w=1600&q=80)` 
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#2a2a2a]/95 via-[#2a2a2a]/85 to-[#2a2a2a]/90" />
        
        {/* Animated Particles/Lines */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 border border-white/10 rounded-full"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-48 h-48 border border-white/10 rounded-full"
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="absolute top-1/2 left-1/4 w-24 h-24 border border-white/5 rounded-full"
            animate={{ scale: [1, 1.5, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 6, repeat: Infinity, delay: 2 }}
          />
          {/* Floating dots */}
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{ 
                top: `${20 + i * 15}%`, 
                left: `${10 + i * 20}%` 
              }}
              animate={{ 
                y: [0, -20, 0],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                duration: 3 + i, 
                repeat: Infinity,
                delay: i * 0.5
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20"
              >
                <motion.div 
                  className="w-2 h-2 bg-green-400 rounded-full"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <span className="text-white/90 text-sm font-medium">Ready to Start Your Project</span>
              </motion.div>

              {/* Heading */}
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              >
                Let's Build the
                <motion.span 
                  className="block mt-2"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-white">
                    Future Together
                  </span>
                </motion.span>
              </motion.h2>

              {/* Description */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed"
              >
                Ready to transform your business with cutting-edge technology? 
                Our experts are here to turn your vision into reality.
              </motion.p>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap justify-center gap-8 mb-10"
              >
                {[
                  { value: '24h', label: 'Response Time' },
                  { value: 'Free', label: 'Consultation' },
                  { value: '100%', label: 'Confidential' }
                ].map((stat, index) => (
                  <motion.div 
                    key={index}
                    className="text-center"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  >
                    <div className="text-2xl md:text-3xl font-bold text-white">{stat.value}</div>
                    <div className="text-white/60 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>

              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-[#320547] hover:bg-gray-100 text-lg px-8 py-6 shadow-xl shadow-black/20 hover:scale-105 transition-transform"
                >
                  <Link to="/contact" className="flex items-center gap-2">
                    Start Your Project 
                    <ArrowRight size={20} />
                  </Link>
                </Button>
                <Link 
                  to="/services"
                  className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-[#320547] text-lg px-8 py-3 rounded-md backdrop-blur-sm hover:scale-105 transition-all font-medium"
                >
                  Explore Our Services
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
